// Wire types mirroring the server's /me/substance/* + /me/flavor/:id/formulas
// substance payloads. Kept in lock-step with apps/nos/server/src/services/
// substanceLens.ts (SubstanceLensInfo), batchSupplyFlags.ts (summary shape),
// and the three modal endpoints (inventory / market-products / safety).
//
// Spec: §4 / §5 / §6 / §8 of 2026-05-11-current-flask-server-data-design.

export type SubstanceKind = "compound" | "food";

export type SubstanceLensInfo = {
  primaryRole:    string | null;
  technicalRoles: string[];
  solubility:     "water" | "oil" | "mixture" | null;
  chemClass:      string | null;
  family:         { code: string; label: string; color: string } | null;
  // All goodscents flavor descriptors associated with the substance. Empty
  // array when no goodscents row matches (compound has no inchikey, or food
  // CAS doesn't appear in the goodscents material list). Both
  // ingredient-search and the formulas/list endpoint emit this — the
  // intent is that ingredient-intrinsic data is fetched identically by
  // both surfaces so the client renders the same color band / chips
  // regardless of where the row landed in the UI.
  flavorLabels:   string[];
};

export type CostInputs = {
  pricePerGramUsd: number | null;
  mw: number | null;
  densityGPerMl: number | null;
};

export type SubstanceSupplyFlags = {
  inventoryTop: { catalogNo: string; sizeLabel: string; quantity: number } | null;
  marketCheapest: {
    supplier:       string;
    productNo:      string;
    packagingLabel: string;
    pricePerGram:   number | null;
    currency:       string;
  } | null;
  safetySummary: { signal: "Danger" | "Warning" | null; pictograms: string[] } | null;
  costInputs: CostInputs;
};

export type InventoryRow = {
  id: string;
  supplier:       { id: number; name: string };
  catalogNo:      string;
  label:          string;
  form:           string | null;
  sizeLabel:      string;
  quantity:       number;
  unitPrice:      number;
  currency:       string;
  unitPriceLabel: string;
  lotNo:          string | null;
  storage:        string | null;
  remarks:        string | null;
  receivedAt:     string | null;
  outOfStockAt:   string | null;
  files:          { coa: boolean; sds: boolean; tds: boolean };
};
export type InventoryResponse = { rows: InventoryRow[] };

export type MarketProductOffer = {
  id:             number;
  productNo:      string | null;
  packagingLabel: string | null;
  packagingSizeG: number | null;
  price:          number;
  currency:       string;
  priceSetAt:     string;
  isLatestForSku: boolean;
  pricePerGram:   number | null;
};
export type MarketProductGroup = {
  groupId:   number;
  supplier:  { id: number; name: string };
  name:      string;
  groupName: string | null;
  form:      string | null;
  cas:       string | null;
  scope:     "noahs_farm" | "own_org" | "own_profile";
  offers:    MarketProductOffer[];
};
export type MarketProductsResponse = { groups: MarketProductGroup[] };

export type SafetyPayload = {
  signal: "Danger" | "Warning" | null;
  pictograms: { code: string; name: string }[];
  hazardStatements:        { code: string; text: string }[];
  precautionaryStatements: { code: string; text: string }[];
  practicalInfo: {
    firstAid: { skin: string[]; eyes: string[]; inhalation: string[]; ingestion: string[] };
    skinEyeRespIrritation: string[];
    handlingStorage:       string[];
    stabilityReactivity:   string[];
  };
  toxicity: { ldOral: string | null; flashPoint: string | null; meltingPoint: string | null };
  references: {
    source: "pubchem_ghs" | "goodscents_material_list" | "compound_usage_safety_hazards";
    rows: number;
  }[];
  rawSources: null;
};

export type FileUrlResponse = { url: string; expiresIn: number };
export type InventoryFileSlot = "coa" | "sds" | "tds";

const SUBSTANCE_KEY_RE = /^(compound|food):([0-9]+)$/;

export function parseSubstanceKey(
  s: string,
): { kind: SubstanceKind; id: number } | null {
  const m = s.match(SUBSTANCE_KEY_RE);
  if (!m || m[1] === undefined || m[2] === undefined) return null;
  const id = Number(m[2]);
  return Number.isFinite(id) ? { kind: m[1] as SubstanceKind, id } : null;
}
